import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Info as InfoIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { read, utils } from 'xlsx';
import api from '../../utils/api';

const DatabaseManagement = () => {
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '', severity: 'info' });
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [backupDownloaded, setBackupDownloaded] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);

  // Carica i dati dell'utente corrente per verificare i permessi
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setCurrentUser(response.data);
        setIsAdmin(['admin', 'superadmin'].includes(response.data.role));
      } catch (err) {
        console.error('Errore nel recupero dei dati utente:', err);
        setIsAdmin(false);
      }
    };
    
    fetchCurrentUser();
  }, []);

  // Funzione per mostrare snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Funzione per gestire la chiusura dello snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Funzione per mostrare un alert informativo
  const showAlert = (message, severity = 'info') => {
    setAlertInfo({ show: true, message, severity });
    setTimeout(() => {
      setAlertInfo(prev => ({ ...prev, show: false }));
    }, 6000); // Auto-hide dopo 6 secondi
  };

  // Funzione per confermare il reset del database
  const handleResetDatabase = async () => {
    setConfirmResetOpen(false);
    setLoading(true);
    
    try {
      const response = await api.post('/settings/database/reset');
      
      if (response.data.success) {
        showSnackbar('Database resettato con successo!', 'success');
        showAlert(
          `Le seguenti tabelle sono state ripulite: ${response.data.tablesCleared.join(', ')}`,
          'success'
        );
      } else {
        showSnackbar('Errore durante il reset del database', 'error');
      }
    } catch (error) {
      console.error('Errore durante il reset del database:', error);
      showSnackbar('Errore durante il reset del database', 'error');
      showAlert(
        `Errore: ${error.response?.data?.message || error.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Funzione per scaricare il template Excel dei clienti
  const handleDownloadCustomersTemplate = async () => {
    try {
      const response = await api.get('/settings/database/template/customers');
      
      if (response.data.success) {
        // Prepara il file Excel
        const worksheet = utils.json_to_sheet(response.data.template);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "Clienti");
        
        // Genera il file Excel e avvia il download
        const excelBuffer = window.XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Crea un URL per il blob e avvia il download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'clienti_template.xlsx';
        link.click();
        
        // Pulisci l'URL creato
        URL.revokeObjectURL(url);
        
        showSnackbar('Template dei clienti scaricato con successo', 'success');
      }
    } catch (error) {
      console.error('Errore durante il download del template clienti:', error);
      showSnackbar('Errore durante il download del template clienti', 'error');
    }
  };

  // Funzione per scaricare il template Excel delle manutenzioni
  const handleDownloadMaintenancesTemplate = async () => {
    try {
      const response = await api.get('/settings/database/template/maintenances');
      
      if (response.data.success) {
        // Prepara il file Excel
        const worksheet = utils.json_to_sheet(response.data.template);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "Manutenzioni");
        
        // Genera il file Excel e avvia il download
        const excelBuffer = window.XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Crea un URL per il blob e avvia il download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'manutenzioni_template.xlsx';
        link.click();
        
        // Pulisci l'URL creato
        URL.revokeObjectURL(url);
        
        showSnackbar('Template delle manutenzioni scaricato con successo', 'success');
      }
    } catch (error) {
      console.error('Errore durante il download del template manutenzioni:', error);
      showSnackbar('Errore durante il download del template manutenzioni', 'error');
    }
  };

  // Funzione per importare i clienti da Excel
  const handleImportCustomers = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Resetta eventuali risultati o errori precedenti
        setImportResult(null);
        setImportError(null);
        setLoading(true);

        // Leggi il file Excel
        const data = e.target.result;
        const workbook = read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet);
        
        // Verifica che ci siano dati
        if (jsonData.length === 0) {
          setImportError('Il file Excel non contiene dati');
          setLoading(false);
          return;
        }
        
        // Invia i dati al server
        const response = await api.post('/settings/database/import/customers', {
          customers: jsonData
        });
        
        // Gestisci la risposta
        if (response.data.success) {
          setImportResult(response.data);
          showSnackbar(`Importati ${response.data.imported} clienti su ${response.data.total}`, 'success');
        } else {
          setImportError(response.data.message || 'Errore durante l\'importazione');
          showSnackbar('Errore durante l\'importazione dei clienti', 'error');
        }
      } catch (error) {
        console.error('Errore durante l\'importazione dei clienti:', error);
        setImportError(error.response?.data?.message || error.message);
        showSnackbar('Errore durante l\'importazione dei clienti', 'error');
      } finally {
        setLoading(false);
        // Reset input file
        event.target.value = '';
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  // Funzione per importare le manutenzioni da Excel
  const handleImportMaintenances = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Resetta eventuali risultati o errori precedenti
        setImportResult(null);
        setImportError(null);
        setLoading(true);

        // Leggi il file Excel
        const data = e.target.result;
        const workbook = read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet);
        
        // Verifica che ci siano dati
        if (jsonData.length === 0) {
          setImportError('Il file Excel non contiene dati');
          setLoading(false);
          return;
        }
        
        // Invia i dati al server
        const response = await api.post('/settings/database/import/maintenances', {
          maintenances: jsonData
        });
        
        // Gestisci la risposta
        if (response.data.success) {
          setImportResult(response.data);
          showSnackbar(`Importate ${response.data.imported} manutenzioni su ${response.data.total}`, 'success');
        } else {
          setImportError(response.data.message || 'Errore durante l\'importazione');
          showSnackbar('Errore durante l\'importazione delle manutenzioni', 'error');
        }
      } catch (error) {
        console.error('Errore durante l\'importazione delle manutenzioni:', error);
        setImportError(error.response?.data?.message || error.message);
        showSnackbar('Errore durante l\'importazione delle manutenzioni', 'error');
      } finally {
        setLoading(false);
        // Reset input file
        event.target.value = '';
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  // Hidden file input references
  const importCustomersInputRef = React.useRef(null);
  const importMaintenancesInputRef = React.useRef(null);
  const restoreInputRef = React.useRef(null);

  // Funzione per scaricare il backup del database
  const handleDownloadBackup = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/database/backup', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_database_${new Date().toISOString().replace(/[:.]/g, '-')}.sqlite`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setBackupDownloaded(true);
      showSnackbar('Backup del database scaricato con successo', 'success');
    } catch (error) {
      showSnackbar('Errore durante il download del backup', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per gestire l'upload e restore del backup
  const handleRestoreBackup = async () => {
    if (!restoreFile) return;
    setLoading(true);
    setRestoreDialogOpen(false);
    try {
      const formData = new FormData();
      formData.append('backup', restoreFile);
      const response = await api.post('/settings/database/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSnackbar(response.data.message || 'Database ripristinato con successo', 'success');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Errore durante il ripristino del database', 'error');
    } finally {
      setLoading(false);
      setRestoreFile(null);
    }
  };

  // Se l'utente non è admin o superadmin, mostra un messaggio di accesso negato
  if (!isAdmin) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <StorageIcon color="primary" />
              <Typography variant="h6">⚙️ Gestione Dati</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Alert 
            severity="warning" 
            icon={<LockIcon />}
            sx={{ mb: 2 }}
          >
            Accesso riservato. Solo gli amministratori possono utilizzare queste funzionalità.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader 
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <StorageIcon color="primary" />
            <Typography variant="h6">⚙️ Gestione Dati</Typography>
          </Box>
        }
      />
      <Divider />
      
      {loading && <LinearProgress />}
      
      <CardContent>
        <Collapse in={alertInfo.show}>
          <Alert 
            severity={alertInfo.severity} 
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setAlertInfo(prev => ({ ...prev, show: false }))}
              >
                <InfoIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {alertInfo.message}
          </Alert>
        </Collapse>
        
        {/* Sezione Reset Database */}
        <Box mb={4}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            🧹 Azzeramento Database
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elimina tutti i dati di clienti, veicoli, manutenzioni, vendite, movimenti di magazzino e movimenti contabili. Questa operazione è irreversibile.<br />
            <strong>Prima di procedere è obbligatorio scaricare un backup del database.</strong>
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownloadBackup}
              disabled={loading}
            >
              Scarica Backup Database
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmResetOpen(true)}
              disabled={loading || !backupDownloaded}
            >
              🔁 Reset Dati
            </Button>
          </Box>
          {!backupDownloaded && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Devi scaricare il backup prima di poter azzerare il database.
            </Alert>
          )}
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Sezione Import/Export Clienti */}
        <Box mb={4}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            📥 Importazione Clienti
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scarica il template Excel, compilalo con i dati dei clienti e caricalo per importarli nel sistema, MASSIMO 500 CLIENTI PER FILE.
          </Typography>
          
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownloadCustomersTemplate}
              disabled={loading}
            >
              ⬇️ Scarica Template Clienti
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={() => importCustomersInputRef.current.click()}
              disabled={loading}
            >
              📤 Importa Clienti da Excel
            </Button>
            
            <input
              type="file"
              accept=".xlsx, .xls"
              ref={importCustomersInputRef}
              onChange={handleImportCustomers}
              style={{ display: 'none' }}
            />
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Sezione Import/Export Manutenzioni */}
        <Box mb={4}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            📥 Importazione Manutenzioni
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scarica il template Excel, compilalo con i dati delle manutenzioni e caricalo per importarle nel sistema.
          </Typography>
          
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownloadMaintenancesTemplate}
              disabled={loading}
            >
              ⬇️ Scarica Template Manutenzioni
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={() => importMaintenancesInputRef.current.click()}
              disabled={loading}
            >
              📤 Importa Manutenzioni da Excel
            </Button>
            
            <input
              type="file"
              accept=".xlsx, .xls"
              ref={importMaintenancesInputRef}
              onChange={handleImportMaintenances}
              style={{ display: 'none' }}
            />
          </Box>
        </Box>
        
        {/* Risultati importazione */}
        {importResult && (
          <Box mt={3}>
            <Alert 
              icon={<CheckIcon />}
              severity="success"
              sx={{ mb: 2 }}
            >
              {importResult.message}
            </Alert>
            
            {importResult.errors && importResult.errors.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="error">
                  Errori durante l'importazione:
                </Typography>
                <List dense>
                  {importResult.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <WarningIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
        
        {/* Errori importazione */}
        {importError && (
          <Box mt={3}>
            <Alert severity="error">
              {importError}
            </Alert>
          </Box>
        )}
        
        {/* Sezione Restore Backup Database */}
        <Box mb={4}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            ♻️ Ripristina Backup Database
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Carica un file di backup SQLite per ripristinare lo stato del database. <strong>Attenzione: questa operazione sovrascriverà tutti i dati attuali!</strong>
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={() => restoreInputRef.current.click()}
              disabled={loading}
            >
              Seleziona Backup da Caricare
            </Button>
            <input
              type="file"
              accept=".sqlite"
              ref={restoreInputRef}
              style={{ display: 'none' }}
              onChange={e => {
                if (e.target.files && e.target.files[0]) {
                  setRestoreFile(e.target.files[0]);
                  setRestoreDialogOpen(true);
                }
              }}
            />
            {restoreFile && (
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {restoreFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
      
      {/* Dialog di conferma per reset database */}
      <Dialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">Conferma Reset Database</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ⚠️ Sei sicuro di voler eliminare tutti i dati? Questa operazione è irreversibile.<br /><br />
            <strong>ATTENZIONE:</strong> Verranno eliminati tutti i dati relativi a:
            <ul>
              <li>Clienti</li>
              <li>Veicoli</li>
              <li>Manutenzioni</li>
              <li>Vendite</li>
              <li>Movimenti di magazzino</li>
              <li>Movimenti contabili</li>
            </ul>
            <strong>Prima di procedere devi aver scaricato il backup del database.</strong>
            <br />
            Gli utenti, le impostazioni e i webhook non saranno modificati.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmResetOpen(false)} 
            color="primary"
            disabled={loading}
          >
            Annulla
          </Button>
          <Button 
            onClick={handleResetDatabase} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Conferma Reset
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog di conferma per restore backup */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => { setRestoreDialogOpen(false); setRestoreFile(null); }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">Conferma Ripristino Backup</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ⚠️ Stai per sovrascrivere l'intero database con il file selezionato.<br /><br />
            <strong>Questa operazione è irreversibile!</strong><br />
            Sei sicuro di voler procedere?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRestoreDialogOpen(false); setRestoreFile(null); }} color="primary" disabled={loading}>
            Annulla
          </Button>
          <Button onClick={handleRestoreBackup} color="error" variant="contained" disabled={loading}>
            Conferma Ripristino
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar per i messaggi */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DatabaseManagement; 